import { useCallback, useEffect, useRef } from 'react';

import { useMutation } from '@apollo/react-hooks';
import { noop } from 'lodash-es';

import { FireOrderIn, IServerOrder } from '@rbi-ctg/menu';
import {
  CommitOrderDocument as CommitOrderDocumentFulFillment,
  GetUserOrdersDocument as GetUserOrdersDocumentFulfillment,
  OrderDocument,
} from 'generated/graphql-gateway';
import {
  CommitOrderDocument,
  GetOrderDocument,
  GetUserOrdersDocument,
} from 'generated/rbi-graphql';
import { useOrderStatus } from 'hooks/order-status';
import { ICommitOrderMutationResponse, ICommitOrderMutationVariables } from 'remote/queries/order';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { OrderSuccessFailureStatuses } from 'state/order/constants';
import { buildCommitDeliveryInput, buildCommitOrderInput } from 'utils/cart';
import { PerformanceMarks, setMark } from 'utils/timing';

import { mapCardType } from './map-card-type';
import { ICommitOrderInput, IUseCommitOrderParams } from './types';

const ORDER_DURATION_ACTION = 'commit';
// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1500;

export type ICommitOptions = ICommitOrderInput;

export const useCommitOrder = ({
  logger,
  onCompleted = noop,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  rbiOrderId,
}: IUseCommitOrderParams) => {
  const { logOrderLatencyDuration, onCommitSuccess } = useOrderContext();
  const auth = useAuthContext();
  const commitOptions = useRef({} as ICommitOptions);
  const { updateFulfillmentOperations } = useLDContext();

  useEffect(() => {
    updateFulfillmentOperations('commitOrder');
  }, [updateFulfillmentOperations]);

  const [fireMutation, mutationResult] = useMutation<
    ICommitOrderMutationResponse,
    ICommitOrderMutationVariables
  >(CommitOrderDocument, {
    awaitRefetchQueries: true,
    refetchQueries: [
      { query: GetOrderDocument, variables: { rbiOrderId } },
      { query: GetUserOrdersDocument },
    ],
  });
  const [fireMutationFulfillment, mutationResultFulfillment] = useMutation<
    ICommitOrderMutationResponse,
    ICommitOrderMutationVariables
  >(CommitOrderDocumentFulFillment, {
    awaitRefetchQueries: true,
    refetchQueries: [
      { query: OrderDocument, variables: { rbiOrderId } },
      { query: GetUserOrdersDocumentFulfillment },
    ],
  });
  const isFulfillmentServiceEnabled = useFlag(LaunchDarklyFlag.ENABLE_FULFILLMENT_SERVICE);

  const logOrderLatency = useCallback(
    (rbiOrder: IServerOrder) => {
      setMark(PerformanceMarks.COMMIT_END);

      logOrderLatencyDuration(ORDER_DURATION_ACTION, rbiOrder);
    },
    [logOrderLatencyDuration]
  );

  const onSuccess = useCallback(
    (rbiOrder: IServerOrder) => {
      onCompleted();
      onCommitSuccess(rbiOrder);
    },
    [onCommitSuccess, onCompleted]
  );

  const queryResult = useOrderStatus({
    failureStatuses: OrderSuccessFailureStatuses.failures,
    logger,
    logOrderLatency,
    onSuccess,
    orderStatusPollInterval,
    rbiOrderId,
    successStatuses: OrderSuccessFailureStatuses.success,
  });

  const { orderStatus, serverOrder } = queryResult;

  const commitOrder = useCallback(
    (options: ICommitOptions) => {
      commitOptions.current = options;
      const delivery = buildCommitDeliveryInput(options.order, auth.user);

      // If using Apple or Google Pay, get the underlying credit card used and set this as the card type used
      options.creditType = mapCardType(options);

      const input = buildCommitOrderInput({
        ...options,
        fireOrderIn: (options.order.fireOrderIn || 0) as FireOrderIn,
        rbiOrderId: rbiOrderId!,
      });

      const skipCoolingPeriod = !!options.skipCoolingPeriod;
      setMark(PerformanceMarks.COMMIT_START);
      const mutation = isFulfillmentServiceEnabled ? fireMutationFulfillment : fireMutation;

      return mutation({ variables: { delivery, input, skipCoolingPeriod } });
    },
    [auth.user, fireMutation, fireMutationFulfillment, isFulfillmentServiceEnabled, rbiOrderId]
  );

  const forceCommitOrder = useCallback(() => {
    return commitOrder({ ...commitOptions.current, skipCoolingPeriod: true });
  }, [commitOrder]);

  return {
    commitOrder,
    commitResult: isFulfillmentServiceEnabled ? mutationResultFulfillment : mutationResult,
    failure: queryResult.failure,
    forceCommitOrder,
    loading: mutationResult.loading || queryResult.loading || mutationResultFulfillment.loading,
    order: queryResult,
    orderStatus,
    serverOrder,
    success: queryResult.success,
  };
};
